import { FC, useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RestoreIcon from "@mui/icons-material/Restore";
import {
	useGetSettings,
	useCreateSetting,
	useUpdateSetting,
	useDeleteSetting,
} from "../../hooks/useSettingsManagement";
import { useGetAccounts } from "../../hooks/useAccounts";
import { useUserStore } from "../../stores/user.store";
import {
	SettingManagement,
	CreateSettingPayload,
	UpdateSettingPayload,
} from "../../domain/settings.interface";
import SettingsModal from "../../components/modals/SettingsModal";
import { MenuItem, Select, FormControl, InputLabel } from "@mui/material";

const SettingsManagementPage: FC = () => {
	const { userInfo } = useUserStore();
	const [showModal, setShowModal] = useState(false);
	const [editingSetting, setEditingSetting] =
		useState<SettingManagement | null>(null);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [selectedAccountId, setSelectedAccountId] = useState<string>("");
	const [applicationFilter, setApplicationFilter] = useState<string>("");

	const { data: accounts, isLoading: isLoadingAccounts } = useGetAccounts();
	const {
		data: settings,
		isLoading,
		error,
	} = useGetSettings(selectedAccountId || undefined);
	const createMutation = useCreateSetting();
	const updateMutation = useUpdateSetting();
	const deleteMutation = useDeleteSetting();

	// Debug: Log accounts data
	useEffect(() => {
		if (accounts) {
			// eslint-disable-next-line no-console
			console.log("Accounts loaded:", accounts);
		}
	}, [accounts]);

	// Check if we're in account-specific view
	const isAccountView = !!selectedAccountId;

	// Helper function to safely display default value
	const formatDefaultValue = (value: string): string => {
		if (typeof value === "object") {
			return JSON.stringify(value);
		}
		return String(value);
	};

	const handleCreateSetting = async (
		data: CreateSettingPayload | UpdateSettingPayload,
	) => {
		try {
			// Add createdBy field for backend
			const createPayload: CreateSettingPayload = {
				...(data as CreateSettingPayload),
				createdBy: userInfo?.id || "",
			};

			// If in account view, include accountId to create AccountSetting
			if (selectedAccountId) {
				createPayload.accountId = selectedAccountId;
				// For AccountSetting, use 'value' field instead of 'default'
				if (data.default !== undefined) {
					createPayload.value = data.default;
				}
			}

			// eslint-disable-next-line no-console
			console.log("Creating setting with payload:", createPayload);

			await createMutation.mutateAsync(createPayload);
			handleCloseModal();
		} catch (err) {
			// Error handled by React Query
		}
	};

	const handleUpdateSetting = async (
		settingId: string,
		data: CreateSettingPayload | UpdateSettingPayload,
	) => {
		try {
			// Add updatedBy field required by backend
			const updatePayload: UpdateSettingPayload = {
				...(data as UpdateSettingPayload),
				updatedBy: userInfo?.id || "",
			};

			// If in account view, include accountId to create/update AccountSetting
			if (isAccountView && selectedAccountId) {
				updatePayload.accountId = selectedAccountId;
				// For AccountSetting, use 'value' field instead of 'default'
				if (data.default !== undefined) {
					updatePayload.value = data.default;
					// Remove 'default' field when updating AccountSetting
					delete updatePayload.default;
				}
			}

			// eslint-disable-next-line no-console
			console.log("Updating setting with payload:", updatePayload);

			await updateMutation.mutateAsync({
				settingId,
				data: updatePayload,
			});
			handleCloseModal();
		} catch (err) {
			// Error handled by React Query
		}
	};

	const handleDeleteSetting = async (settingId: string) => {
		if (window.confirm("Are you sure you want to delete this setting?")) {
			try {
				await deleteMutation.mutateAsync(settingId);
			} catch (err) {
				// Error handled by React Query
			}
		}
	};

	const handleResetToGlobal = async (accountSettingId: string) => {
		if (
			window.confirm(
				"Are you sure you want to reset this setting to the global default?",
			)
		) {
			try {
				await deleteMutation.mutateAsync(accountSettingId);
			} catch (err) {
				// Error handled by React Query
			}
		}
	};

	const handleEditSetting = (setting: SettingManagement) => {
		setEditingSetting(setting);
		setShowModal(true);
	};

	const handleCloseModal = () => {
		setShowModal(false);
		setEditingSetting(null);
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setPageSize(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Filter and paginate
	const filteredSettings = (settings || []).filter((setting) => {
		const matchesSearch =
			setting.name.toLowerCase().includes(search.toLowerCase()) ||
			setting.module.toLowerCase().includes(search.toLowerCase()) ||
			setting.section.toLowerCase().includes(search.toLowerCase()) ||
			setting.description.toLowerCase().includes(search.toLowerCase());
		const matchesApplication =
			!applicationFilter || setting.application === applicationFilter;
		return matchesSearch && matchesApplication;
	});

	const paginatedSettings = filteredSettings.slice(
		page * pageSize,
		(page + 1) * pageSize,
	);

	if (isLoading) return <Typography>Loading settings...</Typography>;
	if (error)
		return <Typography color="error">Error loading settings</Typography>;

	return (
		<Box sx={{ p: 3 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4" component="h1">
					Settings Management
				</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={() => setShowModal(true)}
				>
					Add Setting
				</Button>
			</Box>

			{/* Account Selection Dropdown */}
			<Box mb={3}>
				<FormControl fullWidth>
					<InputLabel id="account-select-label">View Mode</InputLabel>
					<Select
						labelId="account-select-label"
						id="account-select"
						value={selectedAccountId}
						label="View Mode"
						onChange={(e) => {
							setSelectedAccountId(e.target.value);
							setPage(0); // Reset pagination when switching views
						}}
						disabled={isLoadingAccounts}
					>
						<MenuItem value="">
							<Box display="flex" alignItems="center" gap={1}>
								<Typography>Global Settings</Typography>
								<Chip
									label="All Accounts"
									size="small"
									color="primary"
									variant="outlined"
								/>
							</Box>
						</MenuItem>
						{accounts?.map((account) => (
							<MenuItem key={account._id} value={account._id}>
								<Box display="flex" alignItems="center" gap={1}>
									<Typography>{account.name}</Typography>
									<Chip
										label="Account Specific"
										size="small"
										color="secondary"
										variant="outlined"
									/>
								</Box>
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			{/* Search and Application Filter */}
			<Box mb={3} display="flex" gap={2}>
				<TextField
					fullWidth
					label="Search settings"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					InputProps={{
						startAdornment: (
							<SearchIcon sx={{ mr: 1, color: "action.active" }} />
						),
					}}
				/>
				<FormControl sx={{ minWidth: 200 }}>
					<InputLabel id="application-filter-label">Application</InputLabel>
					<Select
						labelId="application-filter-label"
						value={applicationFilter}
						label="Application"
						onChange={(e) => {
							setApplicationFilter(e.target.value);
							setPage(0);
						}}
					>
						<MenuItem value="">All</MenuItem>
						<MenuItem value="backoffice">Backoffice</MenuItem>
						<MenuItem value="mobile">Mobile</MenuItem>
						<MenuItem value="platform">Platform</MenuItem>
					</Select>
				</FormControl>
			</Box>

			{/* Table */}
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Name</TableCell>
							<TableCell>Application</TableCell>
							<TableCell>Module</TableCell>
							<TableCell>Section</TableCell>
							<TableCell>Type</TableCell>
							{isAccountView ? (
								<>
									<TableCell>Global Default</TableCell>
									<TableCell>Account Override</TableCell>
									<TableCell>Effective Value</TableCell>
								</>
							) : (
								<TableCell>Default Value</TableCell>
							)}
							<TableCell>Status</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{paginatedSettings.map((setting) => (
							<TableRow key={setting._id}>
								<TableCell>
									<Typography variant="body2" fontWeight="medium">
										{setting.name}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{setting.description}
									</Typography>
								</TableCell>
								<TableCell>{setting.application}</TableCell>
								<TableCell>{setting.module}</TableCell>
								<TableCell>{setting.section}</TableCell>
								<TableCell>
									<Chip label={setting.type} size="small" />
								</TableCell>
								{isAccountView ? (
									<>
										{/* Global Default */}
										<TableCell>
											<Typography
												variant="body2"
												sx={{
													maxWidth: 150,
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{formatDefaultValue(setting.default)}
											</Typography>
										</TableCell>
										{/* Account Override */}
										<TableCell>
											{setting.accountOverride ? (
												<Box>
													<Typography
														variant="body2"
														sx={{
															maxWidth: 150,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
													>
														{formatDefaultValue(setting.accountOverride.value)}
													</Typography>
													<Chip
														label="Overridden"
														size="small"
														color="warning"
														sx={{ mt: 0.5 }}
													/>
												</Box>
											) : (
												<Chip
													label="Using Global"
													size="small"
													color="default"
													variant="outlined"
												/>
											)}
										</TableCell>
										{/* Effective Value */}
										<TableCell>
											<Typography
												variant="body2"
												fontWeight={setting.accountOverride ? "bold" : "normal"}
												sx={{
													maxWidth: 150,
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{formatDefaultValue(setting.effectiveValue)}
											</Typography>
										</TableCell>
									</>
								) : (
									<TableCell>
										<Typography
											variant="body2"
											sx={{
												maxWidth: 200,
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											}}
										>
											{formatDefaultValue(setting.default)}
										</Typography>
									</TableCell>
								)}
								<TableCell>
									<Chip
										label={setting.active ? "Active" : "Inactive"}
										color={setting.active ? "success" : "default"}
										size="small"
									/>
								</TableCell>
								<TableCell align="right">
									<IconButton
										size="small"
										onClick={() => handleEditSetting(setting)}
										color="primary"
									>
										<EditIcon />
									</IconButton>
									{isAccountView && setting.accountOverride ? (
										<IconButton
											size="small"
											onClick={() =>
												setting.accountOverride &&
												handleResetToGlobal(setting.accountOverride._id)
											}
											color="warning"
											title="Reset to Global Default"
										>
											<RestoreIcon />
										</IconButton>
									) : (
										!isAccountView && (
											<IconButton
												size="small"
												onClick={() => handleDeleteSetting(setting._id)}
												color="error"
											>
												<DeleteIcon />
											</IconButton>
										)
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
				<TablePagination
					component="div"
					count={filteredSettings.length}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={pageSize}
					onRowsPerPageChange={handleChangeRowsPerPage}
					rowsPerPageOptions={[5, 10, 25, 50]}
				/>
			</TableContainer>

			{/* Modal */}
			<SettingsModal
				open={showModal}
				onClose={handleCloseModal}
				onSave={
					editingSetting
						? (data: CreateSettingPayload | UpdateSettingPayload) =>
								handleUpdateSetting(
									editingSetting.accountOverride
										? editingSetting.accountOverride._id
										: editingSetting._id,
									data,
								)
						: handleCreateSetting
				}
				editingSetting={editingSetting}
				isAccountView={isAccountView}
				selectedAccountName={
					accounts?.find((a) => a._id === selectedAccountId)?.name
				}
			/>
		</Box>
	);
};

export default SettingsManagementPage;
